import { X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '../../components/ui/select';
import type { Account } from '../../entities/account';
import { EMPTY_FILTERS, isFiltered, type JournalFilters, type JournalFormat, type JournalPeriod } from './lib/journal';

const ALL = '__all__';

/**
 * Фильтры журнала «Через InstaFarm»: аккаунт или группа пабликов, формат,
 * период. Запоминаются (useRemembered у страницы). Автора у записи журнала
 * нет — по нему фильтровать нечем.
 */
export function JournalFiltersBar({ value, onChange, accounts, groups }: {
  value: JournalFilters;
  onChange: (next: JournalFilters) => void;
  accounts: readonly Account[];
  groups: readonly { id: string; name: string }[];
}) {
  const set = (patch: Partial<JournalFilters>) => onChange({ ...value, ...patch });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={value.account || ALL} onValueChange={(v) => set({ account: v === ALL ? '' : v })}>
        <SelectTrigger className="h-9 w-[220px]" aria-label="Аккаунт">
          <SelectValue placeholder="Все аккаунты" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Все аккаунты</SelectItem>
          {groups.length > 0 && (
            <SelectGroup>
              <SelectLabel>Группы</SelectLabel>
              {groups.map((g) => <SelectItem key={g.id} value={`group:${g.id}`}>{g.name}</SelectItem>)}
            </SelectGroup>
          )}
          <SelectGroup>
            <SelectLabel>Аккаунты</SelectLabel>
            {accounts.map((a) => <SelectItem key={a.id} value={a.id}>@{a.username || a.name}</SelectItem>)}
          </SelectGroup>
        </SelectContent>
      </Select>

      <Select value={value.format} onValueChange={(v) => set({ format: v as JournalFormat })}>
        <SelectTrigger className="h-9 w-[140px]" aria-label="Формат">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Все форматы</SelectItem>
          <SelectItem value="reel">Рилсы</SelectItem>
          <SelectItem value="post">Посты</SelectItem>
        </SelectContent>
      </Select>

      <Select value={value.period} onValueChange={(v) => set({ period: v as JournalPeriod })}>
        <SelectTrigger className="h-9 w-[150px]" aria-label="Период">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">За всё время</SelectItem>
          <SelectItem value="7">7 дней</SelectItem>
          <SelectItem value="30">30 дней</SelectItem>
          <SelectItem value="90">90 дней</SelectItem>
        </SelectContent>
      </Select>

      {isFiltered(value) && (
        <Button variant="ghost" size="sm" onClick={() => onChange(EMPTY_FILTERS)}>
          <X className="size-4" />
          Сбросить
        </Button>
      )}
    </div>
  );
}
